import React, { useContext, useEffect, useState } from 'react';

import {
  Grid,
  Typography,
} from '@material-ui/core';

import {
  useHistory,
  useParams,
  withRouter,
} from 'react-router-dom';

import PropTypes from 'prop-types';

import AddButton from '../../buttons/addButton';
import AddEnrolmentsDialog from '../../modals/addEnrolmentsDialog';
import AlertDialog from '../../modals/alertDialog';
import BreadCrumb from '../../fields/breadCrumb';
import PatientDetailsView from './patientDetailsView';
import PatientEnrolmentList from './patientEnrolmentList';
import CustomSnackbar from '../../feedback/customSnackbar';
import EditButton from '../../buttons/editButton';
import Footer from '../../fields/footer';
import Header from '../../fields/header';
import MenuDrawer from '../../fields/menuDrawer';
import SpinnerLoading from '../../feedback/spinnerLoading';

import { getPatientEnrolments, getPatientInfo, getPrograms, removeEnrolment } from '../../../utils/comms';

import { extractAdministeredProgramsId } from '../../../utils/helpers';

import translations from '../../../languages/i18n';

import generalStyles from '../../../styles/generalStyles';
import { sectionTitle } from '../../../styles/formElementsStyles';

// NOTA: Per poder emprar el react-router-dom i enllaçar pàgines s'ha de convertir el component en una funció i exportar-lo embolicat per withRouter
// En cas de que el component sigui un fill directe de Route no fa falta fer-ho
// Explicat al punt 2.1. Using withRouter de https://dev.to/projectescape/programmatic-navigation-in-react-3p1l

import { SessionContext } from '../../../utils/context';

function PatientInfo({ lang, handleChangeLang }) {
  const sessionContext = useContext(SessionContext);

  const classes = generalStyles();
  const sectionTitleStyle = sectionTitle();

  const [administeredProgramsList, setAdministeredProgramsList] = useState([]);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackType, setFeedbackType] = useState('');
  const [infoReady, setInfoReady] = useState(false);
  const [patientData, setPatientData] = useState({});
  const [patientEnrolments, setPatientEnrolments] = useState({});
  const [programAdministrator, setProgramAdministrator] = useState(false);
  const [selectedEnrolments, setSelectedEnrolments] = useState([]);
  const [showAddEnrolments, setShowAddEnrolments] = useState(false);
  const [showRemoveAlert, setShowRemoveAlert] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showLoading, setShowLoading] = useState(false);

  const history = useHistory();
  const { idPatient } = useParams();

  /* const isProgramAdmin = (idUser, programs) => {
    const programAdmin = programs.some((program) => {
      // Miram si dins els programes n'hi ha algun on el clínic sigui administrador
      return program.accessibleBy.clinicians.find((clinician) => clinician.clinician._id === idUser).admin === true;
    });
    return programAdmin;
  }; */

  const manageFeedback = (fbType, fbMessage) => {
    setFeedbackMessage(translations[`_${fbType}${fbMessage}`]);
    setFeedbackType(fbType);
    setShowFeedback(true);
  };

  useEffect(() => {
    const getInfoFromServer = async () => {
      setShowLoading(true);
      let errorDuringTheProcess = false;
      let typeOfError = 'Generic'; // Valor per defecte
      const responseServerInfo = await Promise.all([
        getPatientInfo(idPatient, sessionContext.userToken),
        getPatientEnrolments(idPatient, sessionContext.userToken), // Retorna tots els enrolments del pacient, independentment de si el clínic forma part de l'equip o no
        getPrograms(sessionContext.userToken), // Obtenim el llistat dels programes als que pertany el clínic. ALERTA: si l'usuari és l'Administrador obtenim tots els programes del sistema
      ]);
      setShowLoading(false);
      // Comprovam si dins les respostes n'hi ha alguna d'erronia
      const allOk = responseServerInfo.map((answer) => {
        if (answer.status && answer.status === 200) return 'ok';
        if (answer === 'timeOut') return 'timeOut';
        return 'error';
      });
      const idTO = allOk.indexOf('timeOut');
      const idE = allOk.indexOf('error');
      if (idTO > -1 || idE > -1) { // Al manco hi ha 1 timeOut o 1 error
        errorDuringTheProcess = true;
        // Miram si algun dels errors rebuts és un timeOut
        if (idTO > -1) typeOfError = 'TimeOut'; // Al manco hi ha hagut un timeOut
      }
      if (!errorDuringTheProcess) { // No hi ha cap error rellevant
        sessionContext.userData = responseServerInfo[0].data.patient;
        setPatientData(responseServerInfo[0].data.patient);
        if (sessionContext.role === 'Clinician') {
          // Si l'usuari és clínic, feim un llistat amb els programes que administra per emprar-ho a PatientEnrolmentList
          const administeredPrograms = extractAdministeredProgramsId(responseServerInfo[2].data.programs, sessionContext.userId);
          setAdministeredProgramsList(administeredPrograms);
          // Comprovam si l'usuari clínic és administrador d'algun programa
          // Si hi ha al manco un programa a l'array vol dir que l'usuari clínic és administrador
          const programAdmin = administeredPrograms.length !== 0;
          setProgramAdministrator(programAdmin);
          // Filtram el llistat d'enrolments del pacient per a que el clínic només pugui veure els de programes que administra o d'equips clínics als que pertany
          const filteredEnrolments = responseServerInfo[1].data.enrolments.filter((enrolment) => (
            administeredPrograms.find((programId) => programId === enrolment.program._id) ||
            enrolment.supervisedBy.some((supervisor) => supervisor.clinician === sessionContext.userId)
          ));
          setPatientEnrolments(filteredEnrolments);
        } else { // L'usuari és l'Administrador del sistema
          setPatientEnrolments(responseServerInfo[1].data.enrolments);
        }
        setInfoReady(true);
      } else { // Hi ha hagut errors durant el procés
        manageFeedback('error', typeOfError);
      }
    };
    sessionContext.userData = null; // Buidam les dades per només guardar les darreres obtingudes del servidor
    getInfoFromServer();
  }, []);

  const editPatient = () => {
    history.push(`/${sessionContext.role.toLowerCase()}/editPatientInfo/${idPatient}`);
  };

  const editEnrolment = (idEnrolment) => {
    history.push(`/${sessionContext.role.toLowerCase()}/editEnrolmentInfo/${idEnrolment}`);
  };

  const gotoScreen = (screen) => {
    history.push(`/${sessionContext.role.toLowerCase()}/${screen}`);
  };

  const obtainPatientEnrolments = async () => {
    setShowLoading(true);
    const response = await getPatientEnrolments(idPatient, sessionContext.userToken);
    setShowLoading(false);
    if (response.status === 200) { // Tenim llistat
      let filteredEnrolments = [];
      if (sessionContext.role === 'Clinician') {
        // Filtram el llistat d'enrolments del pacient per a que el clínic només pugui veure els de programes que administra o d'equips clínics als que pertany
        filteredEnrolments = response.data.enrolments.filter((enrolment) => (
          administeredProgramsList.find((programId) => programId === enrolment.program._id) ||
          enrolment.supervisedBy.some((supervisor) => supervisor.clinician === sessionContext.userId)
        ));
      } else { // L'usuari és l'Administrador del sistema, veu tots els enrolments del pacient
        filteredEnrolments = response.data.enrolments;
      }
      setPatientEnrolments(filteredEnrolments);
    } else if (response === 'timeOut') { // El servidor està caigut o l'usuari no té connexió
      manageFeedback('error', 'TimeOut');
    } else { // Error amb les dades o al servidor
      manageFeedback('error', 'Generic');
    }
  };

  const manageAddEnrolmentsAnswer = (reloadEnrolments) => {
    setShowAddEnrolments(false);
    if (reloadEnrolments) {
      obtainPatientEnrolments();
    }
  };

  const removeEnrolments = async () => {
    setShowLoading(true);
    // Esborram inscripcions
    const responseDelete = await Promise.all(selectedEnrolments.map((element) => removeEnrolment(element.id, sessionContext.userToken)));
    // Comprovam si dins les respostes n'hi ha alguna de timeOut o erronia
    const allOk = responseDelete.map((answer) => {
      if (answer.status && answer.status === 200) return 'ok';
      if (answer === 'timeOut') return 'timeOut';
      return 'error';
    });
    const idTO = allOk.indexOf('timeOut');
    const idE = allOk.indexOf('error');
    if (idTO > -1 || idE > -1) { // Al manco hi ha 1 timeOut o 1 error
      setShowLoading(false);
      // Miram si algun dels errors rebuts és un timeOut
      if (idTO > -1) { // Hi ha hagut un timeOut
        manageFeedback('error', 'TimeOut');
      } else { // Hi ha hagut un error
        manageFeedback('error', 'RemoveEnrolment');
      }
    } else { // No hi ha cap error
      manageFeedback('success', 'RemoveEnrolment');
      // Obtenim llistat actualitzat dels enrolments
      obtainPatientEnrolments();
    }
    setSelectedEnrolments([]);
    // Obtenim llistat actualitzat dels enrolments
    // obtainPatientEnrolments();
  };

  const manageRemoveAlertAnswer = (agree) => {
    setShowRemoveAlert(false);
    if (agree) removeEnrolments();
  };

  const selectedEnrolment = (id, descriptiveName) => {
    const selectedEnrolmentsCopy = selectedEnrolments.slice();
    // Cercam si dins selectedEnrolmentsCopy hi ha l'identificador del enrolment
    const idx = selectedEnrolmentsCopy.findIndex((item) => item.id === id);
    if (idx < 0) selectedEnrolmentsCopy.push({ id, descriptiveName }); // Si idx és -1 vol dir que no ha trobat el valor, l'afegim a la taula
    else selectedEnrolmentsCopy.splice(idx, 1); // L'ha trobat l'eliminam de la taula
    setSelectedEnrolments(selectedEnrolmentsCopy);
  };

  const showMenuDrawer = (show) => {
    setShowDrawer(show);
  };

  return (
    <div className={classes.content}>
      <SpinnerLoading show={showLoading} />
      <Header type={sessionContext.role.toLowerCase()} lang={lang} handleChangeLang={handleChangeLang} handleShowDrawer={(show) => showMenuDrawer(show)} />
      <MenuDrawer type={sessionContext.role.toLowerCase()} showDrawer={showDrawer} handleShowDrawer={(show) => showMenuDrawer(show)} handleGotoScreen={(screen) => gotoScreen(screen)} />
      <AlertDialog open={showRemoveAlert} handleClose={(agree) => manageRemoveAlertAnswer(agree)} title={translations['_removeEnrolmentsAlertTitle']} message={translations['_removeEnrolmentsAlertMessage']} data={selectedEnrolments} field='descriptiveName' />
      <CustomSnackbar show={showFeedback} handleCloseFeedback={(show) => setShowFeedback(show)} type={feedbackType} message={(showFeedback && feedbackMessage) || ''} />
      {/* Pos showAddEnrolments && per evitar que s'executi el useEffect de dins AddEnrolmentsDialog fins que no obro el AddEnrolments */}
      {showAddEnrolments && <AddEnrolmentsDialog enrolmentsData={patientEnrolments} idPatient={idPatient} handleClose={(reloadEnrolments) => manageAddEnrolmentsAnswer(reloadEnrolments)} handleFeedback={(fbType, fbMessage) => manageFeedback(fbType, fbMessage)} open={showAddEnrolments} title={translations['_addEnrolmentsDialogTitle']} />}
      <div className={classes.body}>
        <Grid container direction='column' spacing={2}>
          {/* ----------------------- Fila 0 ----------------------- */}
          <Grid item xs={12}>
            <BreadCrumb role={sessionContext.role.toLowerCase()} home='listPatients' lang={lang} />
          </Grid>
          {/* ----------------------- Fila 1 ----------------------- */}
          <Grid item className={sectionTitleStyle.root}>
            <Grid container justify='space-between' spacing={2}>
              <Grid item>
                <Typography variant='h4' component='div' className={sectionTitleStyle.title}>{translations['_patient']}</Typography>
              </Grid>
              <Grid item>
                {sessionContext.role === 'Admin' && <EditButton handleClick={() => editPatient()} />}
              </Grid>
            </Grid>
          </Grid>
          {/* ----------------------- Fila 2 ----------------------- */}
          <Grid item>
            <Grid container justify='space-between' spacing={2}>
              <Grid item xs={6}>
                <PatientDetailsView extended patientData={patientData} />
              </Grid>
              <Grid item xs={6} />
            </Grid>
          </Grid>
          {/* ----------------------- Fila 3 ----------------------- */}
          <Grid item xs={12}>
            <AddButton disabled={!(sessionContext.role === 'Admin' || programAdministrator)} handleClick={() => setShowAddEnrolments(true)} />
          </Grid>
          {/* ----------------------- Fila 4 ----------------------- */}
          <Grid item>
            <Grid container justify='space-between' spacing={2}>
              <Grid item xs={12}>
                {infoReady && (
                  <PatientEnrolmentList
                    administeredProgramsList={administeredProgramsList}
                    handleRemove={() => setShowRemoveAlert(true)}
                    handleEditEnrolment={(enrolment) => editEnrolment(enrolment)}
                    handleSelectedEnrolment={(id, descriptiveName) => selectedEnrolment(id, descriptiveName)}
                    enrolmentsData={patientEnrolments}
                    selectedEnrolments={selectedEnrolments}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>
      <Footer />
    </div>
  );
}

export default withRouter(PatientInfo);

// Indica i controla els props que se han de passar al component
PatientInfo.propTypes = {
  handleChangeLang: PropTypes.func.isRequired,
  lang: PropTypes.string.isRequired,
};
